import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useBudgetKindOptions } from 'hooks/useBudgetKindOptions';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { useGridPrintAction } from 'components/grid/print';
import {
  ACCOUNTING_MASTER_CHANGED_EVENT,
  acctClassOptions,
} from 'budget/budgetAcctStore';
import { getBudgetRoundByKindCode } from 'budget/budgetRoundStore';
import { rebuildBudgetBalances } from 'budget/budgetRoundApi';
import {
  YEAR_NATURE_OPTIONS,
  ORIGIN_TYPE_OPTIONS,
  INOUT_FILTER_OPTIONS,
  loadBalances,
  filterBalances,
} from 'budget/budgetBalanceStore';
import { isBdApiEnabled } from 'config/bdApiConfig';

const GRID_ID = 'budget-balance';

/**
 * 현액관리 — 예산확정 후 가용·집행가능 잔액 조회
 * - 적재: 예산차수등록 「예산확정」 또는 본 화면 「확정분 재적재」
 * - sumDelta: 성립전 확정→현액, 추경 확정 시 성립전 흡수 / 간주 구분 유지
 * - lastRound: 확정 차수 절대액으로 당해 편성확정 현액 치환
 * - GNK: 배정 없이 현액 → 집행권한매핑 → 집행
 */
const BudgetBalanceInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = getCurrentFiscalYear();

  const [acctMasterTick, setAcctMasterTick] = useState(0);
  const classOptions = useMemo(
    () => [{ value: '', label: '전체' }, ...acctClassOptions(tenantCode)],
    [tenantCode, acctMasterTick]
  );

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      budgetKindCode: '',
      inoutDiv: '',
      yearNature: '',
      originType: '',
      acctClass: '',
      deptKeyword: '',
      projectKeyword: '',
    }),
    [tenantCode, year]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, watch } = methods;
  const watchedFiscalYear = watch('fiscalYear');
  const { kindOptions, syncKindOptions } = useBudgetKindOptions({
    tenantCode,
    fiscalYear: watchedFiscalYear || year,
    getValues,
    setValue,
    autoSelect: false,
  });

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  useEffect(() => {
    const refresh = (e) => {
      if (e.detail?.tenantCode === tenantCode && e.detail?.axis === 'class') {
        setAcctMasterTick((n) => n + 1);
      }
    };
    window.addEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
  }, [tenantCode]);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '수지', field: 'inoutDiv', size: 56, align: 'center' }),
      hiCol.roTextColumn({ header: '당해/이월', field: 'yearNature', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '발생유형', field: 'originType', size: 90 }),
      hiCol.roTextColumn({ header: '결합코드', field: 'budgetKindCode', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '예산구분', field: 'budgetClassNm', size: 80 }),
      hiCol.roTextColumn({ header: '차수명', field: 'roundName', size: 120 }),
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 90 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 120 }),
      hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 90 }),
      hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
      hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 110 }),
      hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 80 }),
      hiCol.roTextColumn({ header: '회계구분', field: 'acctClassNm', size: 90 }),
      hiCol.roNumberColumn({
        header: '확정편성(천원)',
        field: 'confirmedAmt',
        size: 120,
        title: '예산확정 시점 편성액',
      }),
      hiCol.roNumberColumn({ header: '이월유입', field: 'carryInAmt', size: 100 }),
      hiCol.roNumberColumn({ header: '전용입', field: 'transferInAmt', size: 90 }),
      hiCol.roNumberColumn({ header: '전용출', field: 'transferOutAmt', size: 90 }),
      hiCol.roNumberColumn({
        header: '현액(천원)',
        field: 'balanceAmt',
        size: 110,
        title: '확정편성+이월유입+전용입−전용출',
      }),
      hiCol.roNumberColumn({ header: '원인행위', field: 'obligAmt', size: 100 }),
      hiCol.roNumberColumn({ header: '집행액', field: 'execAmt', size: 100 }),
      hiCol.roNumberColumn({
        header: '가용(천원)',
        field: 'availableAmt',
        size: 110,
        title: '현액−원인행위−집행 (집행가능예산)',
      }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
  });

  const handleSearch = useCallback(async () => {
    await syncKindOptions();
    const v = getValues();
    const dims = {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
    };
    try {
      const all = await loadBalances(dims);
      const rows = filterBalances(all, {
        budgetKindCode: v.budgetKindCode,
        inoutDiv: v.inoutDiv,
        yearNature: v.yearNature,
        originType: v.originType,
        acctClass: v.acctClass,
        deptKeyword: v.deptKeyword,
        projectKeyword: v.projectKeyword,
      });
      gridRef.current?.setData?.(rows);
      if (!rows.length) {
        showAlert(
          all.length
            ? `조건에 맞는 현액이 없습니다. (전체 ${all.length}건)`
            : '현액이 없습니다. 예산차수등록에서 예산을 확정하거나 「확정분 재적재」를 실행하세요.',
          'warning'
        );
        return;
      }
      const availSum = rows.reduce((s, r) => s + (Number(r.availableAmt) || 0), 0);
      showAlert(
        `조회했습니다. ${rows.length}건 (전체 ${all.length}건) · 가용합계 ${availSum.toLocaleString()}천원`,
        'success'
      );
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getValues, tenantCode, year, syncKindOptions]);

  /** 이미 예산확정된 차수를 선택해 현액 재적재 */
  const handleRebuild = useCallback(async () => {
    await syncKindOptions();
    const v = getValues();
    const fy = v.fiscalYear || year;
    const kind = String(v.budgetKindCode || '').trim();
    if (!kind) {
      showAlert('재적재할 예산차수(결합코드)를 선택하세요.', 'warning');
      return;
    }
    const round = getBudgetRoundByKindCode(tenantCode, fy, kind);
    if (!round) {
      showAlert('예산차수를 찾을 수 없습니다.', 'warning');
      return;
    }
    if (round.budgetConfirmYn !== 'Y') {
      showAlert(
        '예산확정된 차수만 재적재할 수 있습니다.\n예산차수등록에서 먼저 예산확정하세요.',
        'warning'
      );
      return;
    }
    const ok = await showConfirm(
      `「${round.roundName || kind}」 확정 편성을 현액에 다시 반영할까요?\n(해당 연도의 편성확정·당해 현액을 테넌트 설정(합산증감/최종차수)으로 다시 계산합니다)`,
      'warning'
    );
    if (!ok) return;
    let result;
    try {
      if (isBdApiEnabled()) {
        if (!round.roundUid) {
          showAlert('예산차수 UID가 없습니다. 예산차수등록을 다시 조회하세요.', 'warning');
          return;
        }
        const data = await rebuildBudgetBalances(round.roundUid);
        result = {
          ok: data?.ok !== false && data?.success !== false,
          reason: data?.Msg || data?.message,
          count: data?.count ?? data?.balanceCount ?? 0,
          expenseCount: data?.expenseCount ?? 0,
          incomeCount: data?.incomeCount ?? 0,
          sourceAdjNo: data?.sourceAdjNo,
        };
      } else {
        const { rebuildBalancesOnBudgetConfirm } = await import('budget/budgetBalanceStore');
        result = await rebuildBalancesOnBudgetConfirm({
          tenantCode,
          fiscalYear: fy,
          budgetKindCode: kind,
          confirmedAt: round.budgetConfirmAt,
          confirmedBy: user?.id || user?.name || round.budgetConfirmBy || '',
        });
      }
    } catch (e) {
      showAlert(e?.message || '재적재에 실패했습니다.', 'error');
      return;
    }
    if (!result.ok) {
      showAlert(result.reason || '재적재에 실패했습니다.', 'warning');
      return;
    }
    showAlert(
      `현액 ${result.count}건을 반영했습니다.\n(지출 ${result.expenseCount} · 수입 ${result.incomeCount}${
        result.sourceAdjNo != null ? ` · 조정차수 ${result.sourceAdjNo}` : ''
      })`,
      'success'
    );
    await handleSearch();
  }, [getValues, tenantCode, year, user, handleSearch, syncKindOptions]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '현액',
    getData,
    getDataColumns,
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '현액관리' });
  const { printButton, dialog: printDialog } = useGridPrintAction({
    fileName: '현액관리',
    prefsUserId,
    defaultTitle: '현액관리',
  });

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    {
      id: 'rebuild',
      label: '확정분 재적재',
      title: '해당 연도 편성확정 현액을 테넌트 설정(합산증감/최종차수)으로 다시 계산',
      onClick: handleRebuild,
    },
    printButton,
    excelButton,
  ];

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w100,
        labelWidth: SEARCH_LABEL_W,
        required: true,
      },
      {
        name: 'budgetKindCode',
        label: '예산차수',
        type: 'select',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...kindOptions],
        title: '재적재 시에는 차수 선택 필요',
      },
      {
        name: 'inoutDiv',
        label: '수지',
        type: 'select',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        options: INOUT_FILTER_OPTIONS,
      },
      {
        name: 'yearNature',
        label: '당해/이월',
        type: 'select',
        width: CommonCss.w100,
        labelWidth: SEARCH_LABEL_W,
        options: YEAR_NATURE_OPTIONS,
      },
      {
        name: 'originType',
        label: '발생유형',
        type: 'select',
        width: CommonCss.w110,
        labelWidth: SEARCH_LABEL_W,
        options: ORIGIN_TYPE_OPTIONS,
      },
    ],
    [
      {
        name: 'acctClass',
        label: '회계구분',
        type: 'select',
        width: CommonCss.w110,
        labelWidth: SEARCH_LABEL_W,
        options: classOptions,
      },
      {
        name: 'deptKeyword',
        label: '부서',
        type: 'text',
        width: CommonCss.w120,
        labelWidth: SEARCH_LABEL_W,
        placeholder: '코드·명',
      },
      {
        name: 'projectKeyword',
        label: '사업',
        type: 'text',
        width: CommonCss.w140,
        labelWidth: SEARCH_LABEL_W,
        placeholder: '코드·명',
      },
    ],
  ];

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey="budget-balance"
          prefsUserId={prefsUserId}
          summary={`회계연도 ${watchedFiscalYear || year}`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu={false}
            showModColumn={false}
            enableRowCheck={false}
            enableCut={false}
            enableFindReplace
            allowReplace={false}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            exportFileName="현액관리"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
          />
        </div>
      </GridExportFocusScope>
      {excelDialog}
      {printDialog}
    </PageContainer>
  );
};

const BudgetBalance = () => (
  <GridExportProvider>
    <BudgetBalanceInner />
  </GridExportProvider>
);

export default BudgetBalance;
